import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { SectionTitle } from '~/components/section-title/SectionTitle';
import { useCompanyCountryExposure } from '~/hooks/use-knowledge-graph/useKnowledgeGraph';
import { getKnowledgeGraphSearchParams } from '~/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { appPaths } from '~/routes/app-paths';
import { KnowledgeGraphTabType } from '~/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import { CountryCard } from '~/widgets/theme-widget/tabs/theme-exposure-tab/components/country-card/CountryCard';

import * as S from './GeographicExposureSection.styles';

export interface GeographicExposureSectionProps {
  entityTag: string;
}

export const GeographicExposureSection = ({
  entityTag,
}: GeographicExposureSectionProps) => {
  const { t } = useTranslation(['widget']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    data = [],
    isError,
    isFetching,
    error,
  } = useCompanyCountryExposure({
    entityTag,
  });

  const knowledgeGraphSearchParams = getKnowledgeGraphSearchParams({
    entityTag,
    searchParams,
    subtab: KnowledgeGraphTabType.GeographicExposure,
  }).toString();

  const linkToKnowledgeGraph = `${appPaths.assetOverview}?${knowledgeGraphSearchParams}`;

  if ((!isFetching && !isError && !data.length) || error) {
    return null;
  }

  return (
    <S.SectionRow>
      <SectionTitle
        title={t('widget:assetOverview.geographies')}
        variant="small"
        linkTo={linkToKnowledgeGraph}
      />
      {isFetching ? (
        <S.LoadingSkeleton data-testid="loading-skeleton" />
      ) : (
        <S.GeographicExposureWrapper data-testid="geographic-exposure-wrapper">
          {data.slice(0, 3).map((entity, index) => {
            const entityRank = index + 1;
            return (
              <CountryCard
                code={entity?.code ?? ''}
                key={entity.code}
                rank={entityRank}
                onClick={() => navigate(linkToKnowledgeGraph)}
              />
            );
          })}
        </S.GeographicExposureWrapper>
      )}
    </S.SectionRow>
  );
};
